/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  ЕДИНСТВЕННЫЙ МОДУЛЬ ПРИЛОЖЕНИЯ, КОТОРОМУ ПОЗВОЛЕНО ТРОГАТЬ WEB STORAGE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ни `lib/http.ts`, ни стор, ни вью не обращаются к `localStorage` напрямую —
 * это не стилистика, а стратегия отхода: переход на httpOnly-куки должен
 * означать переписывание **одного этого файла** при неизменных интерцепторе,
 * сторе и вью. Поэтому наружу торчат только глаголы («сохрани пару», «дай
 * refresh», «вычисти»), но нигде — «положи вот в этот Storage под этим ключом».
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ПОЧЕМУ ACCESS-ТОКЕН НИКОГДА НЕ ЛОЖИТСЯ НА ДИСК
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Access-токен — предъявительский и неотзываемый на своём горизонте: сервер
 * проверяет подпись и `exp`, сверяясь с множеством отозванных сессий в памяти
 * процесса. Утёкший access даёт полный доступ на все свои 15 минут, и отозвать
 * его дороже, чем не отдавать. Цена того, что он живёт в модульной переменной,
 * — один сетевой round-trip на загрузку страницы (`auth.bootstrap()`).
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ОТЛИЧИЕ ОТ АДМИН-ПАНЕЛИ: «ЗАПОМНИТЬ МЕНЯ» ЗДЕСЬ НЕ СУЩЕСТВУЕТ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * В админке refresh-токен ложился в `localStorage` или в `sessionStorage` —
 * по галочке на форме входа. Здесь галочки нет и хранилище всегда одно:
 * **`localStorage`, безусловно**, а логин всегда уходит с `rememberMe: true`
 * (см. `stores/auth.ts`), то есть сервер выдаёт 30-дневную refresh-сессию.
 *
 * Это не упрощение ради краткости, а прямое следствие того, что это за
 * приложение. Стойка регистрации — не рабочее место с личным браузером:
 * телефон держат в руке, экран гаснет и блокируется, вкладку случайно
 * закрывают, страницу перезагружают, когда «подвисло», браузер убивает
 * фоновую вкладку под нехватку памяти. `sessionStorage` умирает от каждого
 * из этих событий. Отказ, которого мы избегаем, — регистратор, которого
 * выкинуло на форму входа посреди очереди из сорока человек; риск, который мы
 * принимаем, — refresh-токен, переживающий закрытие браузера на телефоне
 * стойки. Первое случается каждый день, второе лечится кнопкой «Выйти».
 *
 * Отсюда же исчез `isRemembered()`: выводить из хранилища больше нечего.
 */

import type { TokenPair } from '@/types/api'

/**
 * Ключ с префиксом приложения. `localStorage` общий на origin, а в проде
 * админка и стойка вполне могут оказаться на одном хосте под разными путями —
 * общий ключ означал бы, что выход из одной гасит сессию другой.
 */
const REFRESH_KEY = 'scanner.checkin.refresh_token'

/**
 * Access-токен. **Модульная переменная — это и есть хранилище.**
 * Живёт ровно столько, сколько живёт JS-контекст вкладки.
 */
let accessToken: string | null = null

/* ─────────────────────────────────────────────────────────────────────────
   Доступ к Storage — всегда через эти обёртки
   ─────────────────────────────────────────────────────────────────────────
   Обращение к `localStorage` умеет **бросать**, а не возвращать null: Safari
   в приватном режиме, отключённые cookies, iframe без `allow-same-origin`.
   Незащищённый `localStorage.getItem` в таком браузере роняет приложение
   белым экраном ещё до первого рендера. Деградация — «работаем так, будто
   ничего не сохранено», а не падение: сессия проживёт до перезагрузки, что
   всё равно лучше пустого экрана. */

function storage(): Storage | null {
  try {
    return globalThis.localStorage
  } catch {
    return null
  }
}

function read(): string | null {
  try {
    return storage()?.getItem(REFRESH_KEY) ?? null
  } catch {
    return null
  }
}

function write(value: string): void {
  try {
    storage()?.setItem(REFRESH_KEY, value)
  } catch {
    /* QuotaExceeded / приватный режим — молча остаёмся на in-memory access */
  }
}

function remove(): void {
  try {
    storage()?.removeItem(REFRESH_KEY)
  } catch {
    /* см. write */
  }
}

/* ─────────────────────────────────────────────────────────────────────────
   Публичная поверхность
   ───────────────────────────────────────────────────────────────────────── */

/** Access-токен для заголовка `Authorization`. `null` до логина и на холодном старте. */
function getAccessToken(): string | null {
  return accessToken
}

/** Refresh-токен с диска. `null` — предъявлять нечего, мы аноним. */
function getRefreshToken(): string | null {
  return read()
}

/**
 * Кладёт пару токенов: access — в память, refresh — на диск.
 *
 * Параметра `rememberMe` нет и не будет: выбор хранилища здесь не делается
 * (см. шапку файла). Вызывается и после логина, и после каждой ротации.
 */
function save(tokens: TokenPair): void {
  accessToken = tokens.accessToken
  write(tokens.refreshToken)
}

/** Полная зачистка: выход, `SESSION_REVOKED`, провалившийся refresh. */
function clear(): void {
  accessToken = null
  remove()
}

/**
 * Кросс-вкладочный выход.
 *
 * Событие `storage` прилетает **только в другие вкладки** того же origin — не
 * в ту, что писала. Ровно это и нужно: вкладка A нажала «Выйти», вкладка B
 * узнаёт об этом, ничего не опрашивая. На стойке вкладка обычно одна, но
 * «обычно» — не гарантия: страницу открывают повторно, когда первая подвисла.
 *
 * Три условия фильтра, каждое обязательное:
 *   • `storageArea === localStorage` — чужие области нас не касаются;
 *   • `key === null` (это `clear()`) **или** `key === REFRESH_KEY`;
 *   • `newValue === null` — иначе мы приняли бы за выход обычную **ротацию**
 *     токена в соседней вкладке и разлогинили бы человека на ровном месте.
 *
 * Возвращает функцию отписки — на случай HMR и тестов.
 */
function onExternalClear(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {}

  const handler = (event: StorageEvent): void => {
    const local = storage()
    if (local !== null && event.storageArea !== local) return
    if (event.key !== null && event.key !== REFRESH_KEY) return
    if (event.newValue !== null) return

    // Локальное зеркало обязано умереть вместе с диском: иначе эта вкладка
    // осталась бы с живым access-токеном на все свои 15 минут.
    accessToken = null
    cb()
  }

  window.addEventListener('storage', handler)
  return () => window.removeEventListener('storage', handler)
}

/**
 * Единая точка входа: `import { tokenStorage } from '@/lib/token-storage'`.
 * `tokenStorage.clear()` на месте вызова читается однозначно, а `clear()` — нет.
 */
export const tokenStorage = {
  getAccessToken,
  getRefreshToken,
  save,
  clear,
  onExternalClear,
}
